import { StationGroupsIcon } from '@app/components/stations/StationGroupsIcon';
import { StationIcon } from '@app/components/stations/StationIcon';
import { GroupIcon } from '@app/components/users/GroupIcon';
import { UserIcon } from '@app/components/users/UserIcon';
import {
  getDarkSurfaceScrollbarSx,
  getLightSurfaceScrollbarSx,
} from '@app/core/ui/thin-scrollbar-sx';
import { ContentAiIcon } from '@app/modules/content-ai/ContentAiIcon';
import { FragmentIcon } from '@app/modules/fragments/components/FragmentIcon';
import { useEnabledSocialPlatforms } from '@app/modules/social-export/hooks/useEnabledSocialPlatforms';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PersonalVideoIcon from '@mui/icons-material/PersonalVideo';
import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { AlertIcon } from '../screens/alerts/AlertIcon';
import { AdminSidebarGroup } from './AdminSidebarGroup';
import { adminSidebarMessages } from './admin-sidebar-messages';

/**
 * Admin-only column. Stays on screen for every `/admin` route.
 * Integrations are one row per enabled platform.
 */
export function AdminSidebar() {
  const { $t } = useIntl();
  const platforms = useEnabledSocialPlatforms();

  return (
    <Box
      sx={{
        width: 220,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: 1,
        borderColor: 'divider',
        // Recede behind paper panels. grey.100 is a light-mode step; dark mode uses the canvas.
        bgcolor: (theme) =>
          theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : theme.palette.grey[100],
      }}
    >
      <Box
        component="nav"
        aria-label={$t(adminSidebarMessages.navLabel)}
        sx={(theme) => ({
          position: 'sticky',
          top: 0,
          maxHeight: '100vh',
          overflowY: 'auto',
          px: 1.5,
          py: 1.5,
          ...(theme.palette.mode === 'dark'
            ? getDarkSurfaceScrollbarSx()
            : getLightSurfaceScrollbarSx()),
        })}
      >
        <Typography variant="subtitle2" component="div" sx={{ fontWeight: 500, pt: 0.5, pb: 1 }}>
          {$t(adminSidebarMessages.navLabel)}
        </Typography>
        <AdminSidebarGroup
          flushTop
          title={$t(adminSidebarMessages.organization)}
          items={[
            {
              to: '/admin/stations',
              message: adminSidebarMessages.stations,
              icon: <StationIcon fontSize="small" />,
            },
            {
              to: '/admin/station-groups',
              message: adminSidebarMessages.stationGroups,
              icon: <StationGroupsIcon fontSize="small" />,
            },
            {
              to: '/admin/users',
              message: adminSidebarMessages.users,
              icon: <UserIcon fontSize="small" />,
            },
            {
              to: '/admin/groups',
              message: adminSidebarMessages.groups,
              icon: <GroupIcon fontSize="small" />,
            },
          ]}
        />
        <AdminSidebarGroup
          title={$t(adminSidebarMessages.configuration)}
          items={[
            {
              to: '/admin/alerts',
              message: adminSidebarMessages.alerts,
              icon: <AlertIcon fontSize="small" />,
            },
            {
              to: '/admin/fragments',
              message: adminSidebarMessages.fragments,
              icon: <FragmentIcon fontSize="small" />,
            },
            {
              to: '/admin/content-ai',
              message: adminSidebarMessages.contentAi,
              icon: <ContentAiIcon fontSize="small" />,
            },
            {
              to: '/admin/video-analysis',
              message: adminSidebarMessages.videoAnalysis,
              icon: <PersonalVideoIcon fontSize="small" />,
            },
          ]}
        />
        <AdminSidebarGroup
          title={$t(adminSidebarMessages.audit)}
          items={[
            {
              to: '/admin/audit/templates',
              message: adminSidebarMessages.auditTemplates,
              icon: <DesignServicesIcon fontSize="small" />,
              isActive: (pathname: string) => pathname.startsWith('/admin/audit/templates'),
            },
            {
              to: '/admin/audit/materials',
              message: adminSidebarMessages.auditMaterials,
              icon: <Inventory2OutlinedIcon fontSize="small" />,
            },
          ]}
        />
        <AdminSidebarGroup
          title={$t(adminSidebarMessages.integrations)}
          items={[...platforms]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((platform) => ({
              to: `/admin/social-export/${platform.id.toLowerCase()}`,
              label: platform.name,
              icon: platform.Icon({ fontSize: 'small' }),
              isActive: (pathname: string) =>
                pathname
                  .toLowerCase()
                  .startsWith(`/admin/social-export/${platform.id.toLowerCase()}`),
            }))}
        />
      </Box>
    </Box>
  );
}
